import * as React from 'react'
import * as Tooltip from 'react-tooltip'
import { Link } from 'react-router'
import { inject, observer } from 'mobx-react'
import { Modal, ModalHeader, ModalBody } from 'reactstrap'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { AddOrEditLoyaltyTier, IBaseLoyaltyTier } from '@getgreenline/homi-shared'
import { CurrencyInput } from '../../../components/CurrencyInput'
import { FormError } from '../../../components/FormError'
import { Routes } from '../../../constants/Routes'
import { LoyaltyTypes } from '@getgreenline/loyalties'
import { Button, Radio } from 'antd-v4'
import { LoyaltySummarySection } from './LoyaltySummarySection'

import './index.scss'
import { COLORS } from '../../../constants/Colors'
import { BlazeButton } from '../../../components/CustomButtons/BlazeButton'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  loyaltyTier: true | IBaseLoyaltyTier
  onHide: () => void
}

interface State {
  isSaving: boolean
  errorMessage?: string
}

@inject('currentCompanyStore')
@observer
export class AddOrCreateLoyaltyModal extends React.Component<Props, State> {
  loyalty = new AddOrEditLoyaltyTier()

  constructor(props: Props) {
    super(props)
    this.state = {
      isSaving: false,
    }
  }

  componentDidMount() {
    if (this.props.loyaltyTier !== true) {
      Object.assign(this.loyalty, this.props.loyaltyTier)
    }
  }

  hide() {
    if (this.loyalty.changed) {
      if (window.confirm('Are you sure?')) {
        this.props.onHide()
      }
    } else {
      this.props.onHide()
    }
  }

  save() {
    if (this.loyalty.updateAllCustomers) {
      if (
        window.confirm(
          'Are you sure you want to assign all your existing customers to this loyalty tier?',
        )
      ) {
        // Proceed
      } else {
        return
      }
    }

    this.setState({ isSaving: true })
    if (this.loyalty.id) {
      this.props
        .currentCompanyStore!.editLoyaltyTier(
          this.loyalty.id,
          this.loyalty.networkObject,
          this.loyalty.updateAllCustomers,
        )
        .then(() => {
          this.props.onHide()
        })
        .catch((error) => {
          this.setState({ isSaving: false })
          this.setState({
            errorMessage:
              error.response.data.message || 'There was an error editing this loyalty tier',
          })
        })
    } else {
      this.props
        .currentCompanyStore!.addLoyaltyTier(
          this.loyalty.networkObject,
          this.loyalty.updateAllCustomers,
        )
        .then(() => {
          this.props.onHide()
        })
        .catch((error) => {
          this.setState({ isSaving: false })
          this.setState({
            errorMessage:
              error.response.data.message || 'There was an error adding this loyalty tier',
          })
        })
    }
  }

  get isCreditAmountInvalid() {
    const { type, creditAmount, creditAmountPer } = this.loyalty

    if (type !== LoyaltyTypes.PER_SPEND) {
      return false
    }

    return creditAmount >= creditAmountPer
  }

  render() {
    const currentCompanyStore = this.props.currentCompanyStore
    const earningType = this.loyalty.type
    const company = this.props.currentCompanyStore!.company!
    let saveButton: JSX.Element

    if (this.state.isSaving) {
      saveButton = (
        <BlazeButton
          buttonProps={{
            type: 'primary',
            size: 'large',
            disabled: true,
          }}
          titleProps={{ title: 'Saving...' }}
        />
      )
    } else if (!this.loyalty.name) {
      saveButton = (
        <BlazeButton
          buttonProps={{
            type: 'primary',
            size: 'large',
            disabled: true,
          }}
          titleProps={{ title: 'Name required' }}
        />
      )
    } else {
      saveButton = (
        <BlazeButton
          buttonProps={{
            type: 'primary',
            size: 'large',
            onClick: (e) => {
              e.preventDefault()
              this.save()
            },
            disabled: this.isCreditAmountInvalid,
          }}
          titleProps={{ title: 'Confirm' }}
        />
      )
    }

    return (
      <Modal
        isOpen={true}
        toggle={() => this.hide()}
        autoFocus={false}
        size='lg'
        style={{ maxWidth: '850px' }}
      >
        <ModalHeader toggle={() => this.hide()}>
          {this.loyalty.id ? 'Edit loyalty tier' : 'Add loyalty tier'}
        </ModalHeader>
        <ModalBody>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              this.save()
            }}
          >
            <div className='form-group'>
              <div className='row'>
                <div className='col-md-6'>
                  <h5>Name</h5>
                  <p className='text-muted'>For example: Medical, Recreational, Veteran</p>
                </div>
                <div className='col-md-6'>
                  <input
                    className='form-control'
                    value={this.loyalty.name}
                    onChange={(e) => (this.loyalty.name = e.target.value)}
                  />
                </div>
              </div>
            </div>
            <hr />
            <div className='row'>
              <div className='col-md-6'>
                <h5>Description</h5>
                <p></p>
              </div>
              <div className='col-md-6'>
                <div className='form-group'>
                  <input
                    className='form-control'
                    value={this.loyalty.description}
                    onChange={(e) => (this.loyalty.description = e.target.value)}
                  />
                </div>
              </div>
            </div>
            <hr />

            <div className='form-group'>
              <div className='row'>
                <div className='col-md-6'>
                  <h5>Type</h5>
                  <p className='text-muted'>Type of Loyalty</p>
                </div>
                <div className='col-md-6'>
                  <Radio.Group
                    className='mt-4'
                    defaultValue={LoyaltyTypes.PER_VISIT}
                    value={earningType}
                    buttonStyle='solid'
                    size='large'
                    onChange={(e) => (this.loyalty.type = e.target.value)}
                  >
                    <Radio.Button className='px-5' value={LoyaltyTypes.PER_VISIT}>
                      Per visit
                    </Radio.Button>
                    <Radio.Button className='px-5' value={LoyaltyTypes.PER_SPEND}>
                      Per spend
                    </Radio.Button>
                  </Radio.Group>
                </div>
              </div>
            </div>
            <hr />

            <div className='form-group'>
              <div className='row'>
                <div className='col-md-6'>
                  <h5>Point amount</h5>
                  <p className='text-muted'>
                    Choose how many points the customer receives per{' '}
                    <strong>{earningType === LoyaltyTypes.PER_VISIT ? 'visit' : 'spend'}</strong>
                  </p>
                </div>
                <div className='col-md-6'>
                  <label>Customer receives</label>
                  <CurrencyInput
                    className={`form-control ${this.isCreditAmountInvalid && 'input-error'}`}
                    onlyPositiveNumber
                    centValue={this.loyalty.creditAmount}
                    onChange={(value) => (this.loyalty.creditAmount = isNaN(value) ? 0 : value)}
                    usePoints={true}
                  />
                  {this.isCreditAmountInvalid && (
                    <div
                      className='d-flex justify-content-start'
                      style={{ fontSize: '11px', color: COLORS.red500, marginTop: '2px' }}
                    >
                      Point amount should be less than the Per Spent value
                    </div>
                  )}
                  {earningType === LoyaltyTypes.PER_VISIT ? (
                    <label className='mt-2'>points per visit</label>
                  ) : (
                    <label className='d-flex mt-2'>
                      points per
                      <CurrencyInput
                        className='form-control mx-1'
                        onlyPositiveNumber
                        centValue={this.loyalty.creditAmountPer}
                        onChange={(value) =>
                          (this.loyalty.creditAmountPer = isNaN(value) ? 0 : value)
                        }
                        style={{ fontSize: 14, minWidth: 20, width: 70, padding: '0px 4px' }}
                      />
                      spent
                    </label>
                  )}
                </div>
              </div>
            </div>
            <hr />
            <div className='form-group'>
              <div className='row'>
                <div className='col-md-6'>
                  <h5>Settings</h5>
                  <p className='text-muted'>Customize how this loyalty tier works for your store</p>
                </div>
                <div className='col-md-6'>
                  <label>Minimum required purchase</label>
                  <span
                    className='fa fa-info-circle text-muted ml-2'
                    data-tip
                    data-for='min-req-purchase'
                  />
                  <Tooltip id='min-req-purchase' place='right' effect='solid'>
                    <div>
                      How much a customer has to purchase before they are elligible to receive
                      loyalty points.
                    </div>
                    <div>Default: $0.00</div>
                  </Tooltip>
                  <CurrencyInput
                    className='form-control'
                    onlyPositiveNumber
                    centValue={this.loyalty.minimumRequiredPurchase}
                    onChange={(value) => (this.loyalty.minimumRequiredPurchase = value)}
                  />
                  {/* <br />
                  <label>Minimum redeemable credits</label>
                  <CurrencyInput className='form-control'centValue={this.loyalty.minimumRedeemable} onChange={(value) => this.loyalty.minimumRedeemable = value} /> */}
                  {/* <br />
                  <label>Maximum redeemable credits</label>
                  <CurrencyInput className='form-control'centValue={this.loyalty.maximumRedeemable} onChange={(value) => this.loyalty.maximumRedeemable = value} /> */}
                </div>
              </div>
            </div>
            <hr />
            <div className='form-group'>
              <div className='row'>
                <div className='col-md-6'>
                  <h5>Make your default loyalty tier?</h5>
                  <p className='text-muted'>All new customers will be automatically enrolled.</p>
                </div>
                <div className='col-md-6'>
                  {this.loyalty.isDefault ? (
                    <BlazeButton
                      buttonProps={{
                        className: 'w-100',
                        type: 'primary',
                        size: 'large',
                        onClick: (e) => {
                          e.preventDefault()
                          this.loyalty.isDefault = !this.loyalty.isDefault
                        },
                      }}
                      titleProps={{ title: 'Default' }}
                    />
                  ) : (
                    <button
                      className='btn btn-secondary w-100'
                      onClick={(e) => {
                        e.preventDefault()
                        this.loyalty.isDefault = !this.loyalty.isDefault
                      }}
                    >
                      Not default
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className='form-group'>
              <div className='row'>
                <div className='col-md-6'>
                  <h5>Apply to all existing customers?</h5>
                  <p className='text-muted'>
                    All customers will be part of this loyalty tier and start collecting points with
                    each purchase right away.
                  </p>
                </div>
                <div className='col-md-6'>
                  {this.loyalty.updateAllCustomers ? (
                    <BlazeButton
                      buttonProps={{
                        className: 'w-100',
                        type: 'primary',
                        size: 'large',
                        onClick: (e) => {
                          e.preventDefault()
                          this.loyalty.updateAllCustomers = !this.loyalty.updateAllCustomers
                        },
                      }}
                      titleProps={{ title: 'Update all' }}
                    />
                  ) : (
                    <button
                      className='btn btn-secondary w-100'
                      onClick={(e) => {
                        e.preventDefault()
                        this.loyalty.updateAllCustomers = !this.loyalty.updateAllCustomers
                      }}
                    >
                      Do not update all
                    </button>
                  )}
                </div>
              </div>
            </div>
            <hr />
            {this.props.loyaltyTier && this.props.loyaltyTier !== true && (
              <>
                <div className='form-group'>
                  <div className='row'>
                    <div className='col-md-6'>
                      <h5>Attached discounts</h5>
                      <p className='text-muted'>
                        You can attach discounts to this loyalty tier by editing the 'loyalty tier'
                        field when editing discounts.
                      </p>
                      <p className='text-muted'>
                        These discounts will be available on the POS when a customer with this
                        loyalty tier is selected.
                      </p>
                      <p className='text-muted'>
                        You can edit your discounts{' '}
                        <Link to={Routes.DASHBOARD_PRODUCTS_DISCOUNTS(company.id)}>here</Link>
                      </p>
                    </div>
                    <div className='col-md-6'>
                      {this.props.loyaltyTier.discounts.length > 0 ? (
                        <ul>
                          {this.props.loyaltyTier.discounts.map((discount) => {
                            return (
                              <li key={discount.id}>
                                <a
                                  href={Routes.DASHBOARD_PRODUCTS_EDIT_DISCOUNT(
                                    company.id,
                                    discount.id,
                                  )}
                                  target='_blank'
                                  rel='noreferrer'
                                >
                                  {discount.name}
                                </a>
                              </li>
                            )
                          })}
                        </ul>
                      ) : (
                        <div>No discounts</div>
                      )}
                    </div>
                  </div>
                </div>
                <hr />
              </>
            )}
            <div className='form-group pl-4 pr-4'>
              <br />
              <h5>Summary</h5>
              <br />
              <LoyaltySummarySection loyaltyTier={this.loyalty} />
              <br />
            </div>
            <hr />
            <div className='text-right'>
              <BlazeButton
                buttonProps={{
                  className: 'mr-2',
                  type: 'default',
                  size: 'large',
                  onClick: () => this.hide(),
                }}
                titleProps={{
                  title: 'Cancel',
                }}
              />
              {saveButton}
            </div>
            <FormError message={this.state.errorMessage} />
          </form>
        </ModalBody>
      </Modal>
    )
  }
}
